/**
 * Form-state do editor de Persona (Brief 4 · Tela 2 · PR3+PR4 · edição).
 *
 * Mantém um rascunho local dos campos EDITÁVEIS, com dirty-tracking. `buildPatch()`
 * devolve apenas o que mudou em relação à persona carregada (ou `null` se nada
 * mudou), no shape que o `PATCH /v1/personas/:id` aceita. O baseline é reanexado
 * quando a persona recarrega (id/version mudam) — pós-save ou pós-refetch de
 * conflito. Espelha `use-playbook-form`.
 *
 * PR3: Identidade (`name` + `vertical`). PR4: seções de conteúdo —
 * `vocabulary`/`ethicalBoundaries` (objetos com arrays de string) e `examples`
 * (array de pares situation/exemplaryResponse). R15 ao vivo: o backend grava o
 * OBJETO/ARRAY INTEIRO (sem merge de chaves), então o patch envia o objeto
 * completo quando qualquer chave muda. As demais dimensões (voiceProfile,
 * comportamento) entram em PRs seguintes.
 */

import { useEffect, useMemo, useRef, useState } from 'react';

import type {
  PersonaAdversarial,
  PersonaDto,
  PersonaEthicalBoundaries,
  PersonaExample,
  PersonaSide,
  PersonaVocabulary,
  PersonaVertical,
  PersonaVoiceProfile,
  UpdatePersonaBody,
} from './types';
import type {
  ConductConfirmationStyle,
  ConductEscalationPosture,
  ConductGuidanceStyle,
  ConductRegisterPolicy,
  ConductResponseLength,
} from '../conduct/conduct-types';

/** Os 5 campos de conduta, como chaves compartilhadas entre draft, DTO e patch. */
const CONDUCT_DRAFT_KEYS = [
  'conductRegisterPolicy',
  'conductGuidanceStyle',
  'conductResponseLength',
  'conductConfirmationStyle',
  'conductEscalationPosture',
] as const;

export interface PersonaDraft {
  name: string;
  vertical: PersonaVertical;
  vocabulary: PersonaVocabulary;
  ethicalBoundaries: PersonaEthicalBoundaries;
  examples: PersonaExample[];
  voiceProfile: PersonaVoiceProfile;
  sideDefault: PersonaSide;
  adversarialProfileDefault: PersonaAdversarial;
  /**
   * Fase 2 «Conduta e Contexto» — os 5 eixos neste nível. `null` = «não opinei, herda o
   * nível acima». É rascunho como qualquer outro campo: o `buildPatch` compara com o
   * servidor e ENVIA o `null` quando o usuário solta a opinião.
   */
  conductRegisterPolicy: ConductRegisterPolicy | null;
  conductGuidanceStyle: ConductGuidanceStyle | null;
  conductResponseLength: ConductResponseLength | null;
  conductConfirmationStyle: ConductConfirmationStyle | null;
  conductEscalationPosture: ConductEscalationPosture | null;
}

/**
 * Snapshot dos campos editáveis a partir do DTO carregado. Arrays/objetos são
 * CLONADOS (cópia rasa de cada array) — o rascunho nunca muta o objeto da query.
 */
export function toDraft(persona: PersonaDto): PersonaDraft {
  return {
    name: persona.name,
    vertical: persona.vertical,
    vocabulary: {
      preferredTerms: [...persona.vocabulary.preferredTerms],
      avoidedTerms: [...persona.vocabulary.avoidedTerms],
    },
    ethicalBoundaries: {
      redirectTopics: [...persona.ethicalBoundaries.redirectTopics],
      toneLimits: [...persona.ethicalBoundaries.toneLimits],
    },
    examples: persona.examples.map((e) => ({ ...e })),
    voiceProfile: { ...persona.voiceProfile },
    sideDefault: persona.sideDefault,
    adversarialProfileDefault: persona.adversarialProfileDefault,
    conductRegisterPolicy: persona.conductRegisterPolicy,
    conductGuidanceStyle: persona.conductGuidanceStyle,
    conductResponseLength: persona.conductResponseLength,
    conductConfirmationStyle: persona.conductConfirmationStyle,
    conductEscalationPosture: persona.conductEscalationPosture,
  };
}

/** Cria um exemplo novo vazio (par situação/resposta exemplar). */
export function makeExample(): PersonaExample {
  return { situation: '', exemplaryResponse: '' };
}

function jsonEq(a: unknown, b: unknown): boolean {
  return JSON.stringify(a) === JSON.stringify(b);
}

/** Normaliza um exemplo a uma ordem de chaves fixa (jsonEq é sensível à ordem). */
function normalizeExample(e: PersonaExample): PersonaExample {
  return { situation: e.situation, exemplaryResponse: e.exemplaryResponse };
}

/**
 * Diff entre a persona carregada e o rascunho. Inclui um campo só se mudou.
 * Retorna `null` quando nada mudou (base do dirty-tracking). `name` é trimado
 * (campo curto). Para `vocabulary`/`ethicalBoundaries` os arrays são comparados
 * individualmente — o serializer pode entregar as chaves em ordem diferente do
 * snapshot, e jsonEq é sensível à ordem das chaves (senão a tela abre "suja" sem
 * nenhuma edição). Quando QUALQUER chave do objeto muda, o objeto INTEIRO é
 * enviado (R15: o backend substitui o objeto, não faz merge).
 */
export function buildPatch(persona: PersonaDto, draft: PersonaDraft): UpdatePersonaBody | null {
  const patch: UpdatePersonaBody = {};

  const name = draft.name.trim();
  if (name !== persona.name) patch.name = name;
  if (draft.vertical !== persona.vertical) patch.vertical = draft.vertical;

  if (
    !jsonEq(draft.vocabulary.preferredTerms, persona.vocabulary.preferredTerms) ||
    !jsonEq(draft.vocabulary.avoidedTerms, persona.vocabulary.avoidedTerms)
  ) {
    patch.vocabulary = {
      preferredTerms: draft.vocabulary.preferredTerms,
      avoidedTerms: draft.vocabulary.avoidedTerms,
    };
  }

  if (
    !jsonEq(draft.ethicalBoundaries.redirectTopics, persona.ethicalBoundaries.redirectTopics) ||
    !jsonEq(draft.ethicalBoundaries.toneLimits, persona.ethicalBoundaries.toneLimits)
  ) {
    patch.ethicalBoundaries = {
      redirectTopics: draft.ethicalBoundaries.redirectTopics,
      toneLimits: draft.ethicalBoundaries.toneLimits,
    };
  }

  const normalizedExamples = draft.examples.map(normalizeExample);
  const baselineExamples = persona.examples.map(normalizeExample);
  if (!jsonEq(normalizedExamples, baselineExamples)) patch.examples = normalizedExamples;

  // voiceProfile: 3 strings; compara por campo (insensível à ordem de chaves) e
  // envia o objeto inteiro (R15: backend substitui o objeto).
  if (
    draft.voiceProfile.formality !== persona.voiceProfile.formality ||
    draft.voiceProfile.warmth !== persona.voiceProfile.warmth ||
    draft.voiceProfile.pace !== persona.voiceProfile.pace
  ) {
    patch.voiceProfile = {
      formality: draft.voiceProfile.formality,
      warmth: draft.voiceProfile.warmth,
      pace: draft.voiceProfile.pace,
    };
  }

  if (draft.sideDefault !== persona.sideDefault) patch.sideDefault = draft.sideDefault;
  if (draft.adversarialProfileDefault !== persona.adversarialProfileDefault) {
    patch.adversarialProfileDefault = draft.adversarialProfileDefault;
  }

  // Fase 2 — os 5 eixos de conduta. Comparação simples e `null` SEMPRE enviado quando
  // mudou: é assim que «voltar a herdar» chega ao banco (o DTO do Engine aceita `null` nas
  // 5 colunas e limpa a coluna; campo ausente não altera nada).
  for (const key of CONDUCT_DRAFT_KEYS) {
    if (draft[key] !== persona[key]) {
      // Cast: TS não correlaciona a chave do laço com o valor por união.
      (patch as Record<string, unknown>)[key] = draft[key];
    }
  }

  return Object.keys(patch).length > 0 ? patch : null;
}

export interface PersonaForm {
  draft: PersonaDraft;
  set: <K extends keyof PersonaDraft>(key: K, value: PersonaDraft[K]) => void;
  isDirty: boolean;
  reset: () => void;
  buildPatch: () => UpdatePersonaBody | null;
}

export function usePersonaForm(persona: PersonaDto): PersonaForm {
  const baselineRef = useRef(persona);
  const [draft, setDraft] = useState<PersonaDraft>(() => toDraft(persona));

  // Reanexa o baseline e descarta o rascunho quando a persona recarrega
  // (pós-save → version++ ; pós-refetch de conflito → version do servidor).
  useEffect(() => {
    baselineRef.current = persona;
    setDraft(toDraft(persona));
  }, [persona.id, persona.version]);

  const set = <K extends keyof PersonaDraft>(key: K, value: PersonaDraft[K]): void => {
    setDraft((prev) => ({ ...prev, [key]: value }));
  };

  const patch = useMemo(() => buildPatch(baselineRef.current, draft), [draft]);

  return {
    draft,
    set,
    isDirty: patch !== null,
    reset: () => setDraft(toDraft(baselineRef.current)),
    buildPatch: () => buildPatch(baselineRef.current, draft),
  };
}
